import { useState } from 'react';
import {
  CardContent,
  Grid,
  Button,
  Box,
  Typography,
  DialogContent,
} from '@mui/material';
import { IOfflinePayemntType } from '../allInvoice/types/invoiceType';
import CommonPdfAndImageVeiw from 'src/components/pdfAndImageView/CommonPdfAndImageVeiw';
import { getStatusLabel } from 'src/Utils/Combined';
import CommonDetailItem, {
  formatDate,
  formatValue,
} from 'src/components/CommonDetailItem/CommonDetailItem';
import ModalsWrapper from 'src/components/Modals/ModalsWrapper';

type IProps = {
  loading?: boolean;
  data: IOfflinePayemntType;
};

const labelWidth = { xs: '130px', sm: '160px', md: '180px' };

const formatKeyLabel = (key: string): string => {
  const result = key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim();
  return result
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const OfflinePaymentCardInformation = ({ data, loading }: IProps) => {
  const [showModal, setShowModal] = useState<boolean>(false);

  if (loading || !data) {
    return null;
  }

  let parsedTranDetails: Record<string, any> | null = null;
  if (data?.tran_details) {
    if (typeof data.tran_details === 'string') {
      try {
        parsedTranDetails = JSON.parse(data.tran_details);
      } catch (e) {
        parsedTranDetails = null;
      }
    } else if (typeof data.tran_details === 'object') {
      parsedTranDetails = data.tran_details;
    }
  }

  const depositDate = formatDate(data?.deposit_date, 'Do-MM-YYYY');
  const paymentDate = formatDate(data?.payment_date, 'Do-MM-YYYY hh:mm A');

  const bankSlip = data?.bank_slip ? (
    <CommonPdfAndImageVeiw file={data.bank_slip} fileName='View Bank Slip' />
  ) : (
    'N/A'
  );

  const paymentType =
    data?.type === 'online' ? 'Online' : data?.type ? 'Offline' : 'N/A';

  const serviceType = data?.service_type
    ? data.service_type.replace(/_/g, ' ')
    : 'N/A';

  const statusLabel = data?.status ? getStatusLabel(data.status) : 'N/A';

  return (
    <>
      <CardContent>
        <Grid container spacing={2}>
          <CommonDetailItem
            label='Account Name'
            value={formatValue(data?.account_name)}
            labelWidth={labelWidth}
            xs={12}
            sm={6}
            md={4}
          />
          <CommonDetailItem
            label='Account No'
            value={formatValue(data?.bank_ac_no)}
            labelWidth={labelWidth}
            xs={12}
            sm={6}
            md={4}
          />
          <CommonDetailItem
            label='Amount'
            value={
              data?.amount
                ? `${Number(data.amount).toLocaleString()} BDT`
                : 'N/A'
            }
            labelWidth={labelWidth}
            xs={12}
            sm={6}
            md={4}
          />
          <CommonDetailItem
            label='Deposit Date'
            value={depositDate}
            labelWidth={labelWidth}
            xs={12}
            sm={6}
            md={4}
          />
          <CommonDetailItem
            label='Payment Date'
            value={paymentDate}
            labelWidth={labelWidth}
            xs={12}
            sm={6}
            md={4}
          />
          <CommonDetailItem
            label='Bank Name'
            value={formatValue(data?.bank_name)}
            labelWidth={labelWidth}
            xs={12}
            sm={6}
            md={4}
          />
          <CommonDetailItem
            label='Branch Name'
            value={formatValue(data?.branch_name)}
            labelWidth={labelWidth}
            xs={12}
            sm={6}
            md={4}
          />
          <CommonDetailItem
            label='Bank Slip'
            value={bankSlip}
            labelWidth={labelWidth}
            xs={12}
            sm={6}
            md={4}
          />
          <CommonDetailItem
            label={'Refercence No'}
            value={formatValue(data?.reference_no)}
            labelWidth={labelWidth}
            xs={12}
            sm={6}
            md={4}
          />
          <CommonDetailItem
            label={'Transaction No'}
            value={formatValue(data?.tran_id)}
            labelWidth={labelWidth}
            xs={12}
            sm={6}
            md={4}
          />
          <CommonDetailItem
            label='Payment Type'
            value={paymentType}
            labelWidth={labelWidth}
            xs={12}
            sm={6}
            md={4}
          />
          <CommonDetailItem
            label='Service Type'
            value={serviceType}
            labelWidth={labelWidth}
            xs={12}
            sm={6}
            md={4}
          />
          <CommonDetailItem
            label='Status'
            value={statusLabel}
            labelWidth={labelWidth}
            xs={12}
            sm={6}
            md={4}
          />
          {parsedTranDetails && (
            <CommonDetailItem
              label='Transaction Details'
              value={
                <Button
                  variant='outlined'
                  size='small'
                  onClick={() => setShowModal(true)}
                >
                  View Details
                </Button>
              }
              labelWidth={labelWidth}
              xs={12}
              sm={6}
              md={4}
            />
          )}
        </Grid>
      </CardContent>

      {parsedTranDetails && (
        <ModalsWrapper
          modalData={{ title: 'Transaction Details' }}
          showModal={showModal}
          setShowModal={setShowModal}
          maxWidth='md'
        >
          <DialogContent dividers sx={{ py: 1.5, px: 2 }}>
            <Grid container spacing={0.5}>
              {Object.entries(parsedTranDetails).map(([key, val]) => (
                <Grid item xs={12} key={key}>
                  <Grid container spacing={1} alignItems='flex-start'>
                    <Grid item xs={5.5} sm={4.5} sx={{ textAlign: 'right' }}>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ fontWeight: 600 }}
                      >
                        {formatKeyLabel(key)} :
                      </Typography>
                    </Grid>
                    <Grid item xs={6.5} sm={7.5} sx={{ textAlign: 'left' }}>
                      <Typography
                        variant='body2'
                        color='text.primary'
                        sx={{ fontWeight: 500, wordBreak: 'break-word' }}
                      >
                        {val !== null && val !== undefined && val !== ''
                          ? typeof val === 'object'
                            ? JSON.stringify(val)
                            : String(val)
                          : 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
        </ModalsWrapper>
      )}
    </>
  );
};

export default OfflinePaymentCardInformation;
